import { useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useDebounce } from "use-debounce";
import { useTran } from "@/hooks";
import { DataTable } from "@/components/common/DataTable";
import { buildTableDefaults } from "@/components/common/data-table-helpers";
import { ROLES } from "@/lib/constants/options";
import { formatDateTime, truncateText } from "@/lib/utils";
import { useAuthStore } from "@/features/auth";
import { useActivityLogStore } from "../hooks";

const ActivityLogTable = observer(() => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const {
    logs,
    loading,
    sort,
    page,
    pageSize,
    total,
    filters,
    setPage,
    setPageSize,
    setSort,
    openDetails,
    loadLogs,
  } = useActivityLogStore();

  const userId = user?.userId || user?.id || "";
  const companyId = user?.companyId || "";

  const isAdminRole = [
    ROLES.SYSTEM_ADMIN,
    ROLES.BUSINESS_UNIT,
    ROLES.SUPER_ADMIN,
  ].includes(user?.role);
  const [debouncedSearch] = useDebounce(filters.searchText, 400);

  useEffect(() => {
    if (!userId) return;
    loadLogs({
      userId,
      companyId,
      isAdminRole,
      searchText: debouncedSearch,
    });
  }, [
    userId,
    companyId,
    isAdminRole,
    page,
    pageSize,
    sort.key,
    sort.direction,
    filters.module,
    filters.startDate,
    filters.endDate,
    filters.selectedUserId,
    debouncedSearch,
    loadLogs,
  ]);

  const { emptyMessage, loadingMessage, getRowProps } = buildTableDefaults({
    t,
    emptyMessageKey: "No activity logs found",
  });

  const columns = useMemo(
    () => [
      {
        key: "index",
        header: t("No."),
        width: "64px",
        render: (_log, index) => (page - 1) * pageSize + index + 1,
      },
      {
        key: "updatedBy",
        header: t("Updated By"),
        render: (log) =>
          log.user?.name || log.user?.username || log.user?.email || "—",
      },
      {
        key: "module",
        header: t("Module"),
        sortable: true,
        sortKey: "module",
        render: (log) => log.moduleTitle || "—",
      },
      {
        key: "action",
        header: t("Action"),
        sortable: true,
        sortKey: "action",
        render: (log) => log.action || "—",
      },
      {
        key: "details",
        header: t("Description"),
        sortable: true,
        sortKey: "details",
        render: (log) => {
          const { text, isTruncated } = truncateText(log.details, 70);
          return (
            <div className="flex items-center gap-1">
              <span className="">{text || "—"}</span>
              {isTruncated ? (
                <button
                  type="button"
                  onClick={() => openDetails(log.details)}
                  className="text-xs text-primary hover:underline"
                >
                  {t("View More")}
                </button>
              ) : null}
            </div>
          );
        },
      },
      {
        key: "createdAt",
        header: t("Date"),
        sortable: true,
        sortKey: "created_at",
        render: (log) => formatDateTime(log.createdAt),
      },
    ],
    [openDetails, page, pageSize, t],
  );

  const handlePageChange = (nextPage) => {
    if (nextPage !== page) {
      setPage(nextPage);
    }
  };

  const handlePageSizeChange = (nextSize) => {
    if (nextSize !== pageSize) {
      setPageSize(nextSize);
      setPage(1);
    }
  };

  const handleSort = (key) => {
    let direction = "asc";
    if (sort.key === key && sort.direction === "asc") {
      direction = "desc";
    }
    setSort({ key, direction });
    setPage(1);
  };

  return (
    <DataTable
      columns={columns}
      data={logs}
      loading={loading}
      sort={sort}
      onSort={handleSort}
      emptyMessage={emptyMessage}
      loadingMessage={loadingMessage}
      getRowKey={(row, index) => row.id || index}
      getRowProps={getRowProps}
      pagination={{
        page,
        pageSize,
        total,
        onPageChange: handlePageChange,
        onPageSizeChange: handlePageSizeChange,
        pageSizeOptions: [10, 30, 50],
        disabled: loading,
        labels: {
          itemsPerPage: t("pagination.itemsPerPage"),
          previous: t("Previous"),
          next: t("Next"),
          pageLabel: ({ page: current, totalPages }) =>
            t("pagination.pageLabel", {
              page: current,
              totalPages,
            }),
        },
      }}
    />
  );
});

export default ActivityLogTable;
